<template>
    <div class="mev-rewards-claimer">
        <div v-if="!walletConnected" class="text-muted small">
            Connect your wallet to check and claim your MEV rewards.
        </div>

        <div v-else-if="loadingAccounts" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="small text-muted ms-2">Loading MEV data...</span>
        </div>

        <div v-else>
            <!-- Claimable + Pending Stats -->
            <div class="row g-3 mb-3">
                <div class="col-6">
                    <div class="compass-stat-card compass-stat-card--compact compass-stat-card--positive">
                        <div class="stat-label">Claimable MEV</div>
                        <div class="stat-value font-mono-num">{{ totalClaimableMev.toFixed(4) }}</div>
                        <div class="stat-unit">SOL ≈ ${{ (totalClaimableMev * solPrice).toFixed(2) }}</div>
                    </div>
                </div>
                <div class="col-6">
                    <div class="compass-stat-card compass-stat-card--compact">
                        <div class="stat-label">Pending This Epoch</div>
                        <div class="stat-value font-mono-num">{{ pendingMev.toFixed(4) }}</div>
                        <div class="stat-unit">SOL ≈ ${{ (pendingMev * solPrice).toFixed(2) }}</div>
                    </div>
                </div>
            </div>

            <!-- Epoch Progress -->
            <div class="small text-muted mb-2">
                <span v-if="timeLeftInEpoch">Claimable in {{ timeLeftInEpoch }}</span>
                <span v-if="nextTipCountdown" class="ms-2">· Next tip: {{ nextTipCountdown }}</span>
            </div>

            <!-- Block Progress Bar -->
            <div v-if="totalMevSlots > 0" class="mb-3">
                <div class="d-flex justify-content-between small text-muted mb-1">
                    <span>Block Progress</span>
                    <span class="font-mono-num">{{ blocks.length }}/{{ totalMevSlots }} blocks</span>
                </div>
                <div class="progress-inline">
                    <div class="progress-bar" :style="{ width: blockProgressPct + '%' }"></div>
                </div>
            </div>

            <!-- Claim Button -->
            <button
                class="compass-btn compass-btn--primary compass-btn--sm w-100 mb-3"
                :disabled="totalClaimableMev === 0 || claiming"
                @click="claimMevRewards"
            >
                <span v-if="claiming" class="spinner-border spinner-border-sm me-1"></span>
                {{ claiming ? 'Claiming...' : 'Claim All MEV Rewards' }}
            </button>

            <div v-if="claimError" class="alert alert-danger small py-2 mb-3">{{ claimError }}</div>
            <div v-if="claimSuccess" class="alert alert-success small py-2 mb-3">
                Claimed {{ claimSuccess.amount }} SOL!
                <a :href="claimSuccess.explorerUrl" target="_blank">View tx &rarr;</a>
            </div>

            <!-- Stake Accounts with MEV -->
            <div v-if="stakeAccountsWithMev.length > 0">
                <label class="form-label fw-bold small mb-2">Your Stake Accounts</label>
                <div class="table-mobile-scroll">
                    <table class="compass-table" style="font-size: 0.85rem;">
                        <thead>
                            <tr>
                                <th>Validator</th>
                                <th class="text-end">Claimable</th>
                                <th class="text-end">Pending</th>
                                <th class="text-end">Progress</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="account in stakeAccountsWithMev" :key="account.pubkey?.toBase58()">
                                <td>
                                    <div class="d-flex align-items-center">
                                        <img
                                            v-if="account.validator?.avatar_url"
                                            :src="account.validator.avatar_url"
                                            class="me-2 rounded-circle"
                                            style="width: 20px; height: 20px"
                                        >
                                        <span class="small">{{ account.validator?.name || shortenAddress(account.validator?.vote_account) }}</span>
                                    </div>
                                </td>
                                <td class="text-end font-mono-num">
                                    <span v-if="account.mevBalance > 0" class="pct-change pct-positive">
                                        +{{ account.mevBalance.toFixed(4) }}
                                    </span>
                                    <span v-else class="text-muted">—</span>
                                </td>
                                <td class="text-end font-mono-num">
                                    <span v-if="account.pendingMev > 0">{{ (account.pendingMev / 1e9).toFixed(4) }}</span>
                                    <span v-else class="text-muted">—</span>
                                </td>
                                <td class="text-end">
                                    <div v-if="account.progress > 0" class="d-flex align-items-center justify-content-end gap-1">
                                        <div class="progress-inline" style="width: 50px;">
                                            <div class="progress-bar" :style="{ width: account.progress + '%' }"></div>
                                        </div>
                                        <span class="font-mono-num small">{{ Math.round(account.progress) }}%</span>
                                    </div>
                                    <span v-else class="text-muted small">—</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Recent MEV Blocks -->
            <div v-if="blocks.length > 0" class="mt-3">
                <label class="form-label fw-bold small mb-2">Recent MEV Blocks</label>
                <div class="table-mobile-scroll">
                    <table class="compass-table" style="font-size: 0.8rem;">
                        <thead>
                            <tr>
                                <th>Slot</th>
                                <th>Validator</th>
                                <th class="text-end">Tips</th>
                                <th class="text-end">Your Share</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="block in recentBlocks" :key="block.slot">
                                <td>
                                    <a :href="'https://explorer.solana.com/block/' + block.slot"
                                       target="_blank"
                                       class="font-mono small">
                                        {{ block.slot }}
                                    </a>
                                </td>
                                <td class="small">{{ validatorData[block.leader?.leader]?.name || shortenAddress(block.leader?.leader) }}</td>
                                <td class="text-end font-mono-num">{{ ((block.fees?.jito_total || 0) / 1e9).toFixed(4) }}</td>
                                <td class="text-end font-mono-num">{{ calculateUserShare(block).toFixed(4) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-if="blocks.length > 10" class="text-muted small mt-1">
                    Showing 10 of {{ blocks.length }} blocks this epoch
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useWallet } from 'solana-wallets-vue'
import { Connection, StakeProgram, Transaction } from '@solana/web3.js'
import axios from 'axios'
import collect from 'collect.js'

export default {
    name: 'MevRewardsClaimer',

    setup() {
        const rpcUrl = window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com'
        const wsUrl = rpcUrl.replace('https', 'wss')
        const connection = new Connection(rpcUrl)

        const wallet = useWallet()
        const solPrice = ref(0)
        const claiming = ref(false)
        const claimError = ref(null)
        const claimSuccess = ref(null)
        const nextTipCountdown = ref('')
        const pendingMev = ref(0)
        const stakeAccounts = ref([])
        const validatorData = ref({})
        const epochInfo = ref({})
        const timeLeftInEpoch = ref('')
        const loadingAccounts = ref(true)
        const currentEpochStartSlot = ref(0)
        const currentEpochInfo = ref(null)
        const currentSlot = ref(0)
        const currentSchedule = ref(null)
        const totalMevSlots = ref(0)
        const nextMevSlot = ref(0)
        const nextTipSlot = ref(Infinity)
        const blocks = ref([])
        const completedBlocks = ref(0)
        const totalBlocks = ref(0)
        const blockProgress = ref(0)
        const avgSlotTime = ref(0)

        // Interval/WS handles for cleanup
        let intervals = []
        let wsConnection = null
        let unmounted = false

        const shortenAddress = (addr) => {
            if (!addr) return '—'
            return addr.slice(0, 4) + '...' + addr.slice(-4)
        }

        const blockProgressPct = computed(() => {
            if (totalMevSlots.value === 0) return 0
            return Math.min(100, (blocks.value.length / totalMevSlots.value) * 100)
        })

        const recentBlocks = computed(() => blocks.value.slice(0, 10))

        const getStakeAccounts = async () => {
            if (!wallet.connected.value) return

            loadingAccounts.value = true
            try {
                const stakeAccountInfo = await connection.getParsedProgramAccounts(
                    StakeProgram.programId,
                    {
                        filters: [
                            { memcmp: { offset: 44, bytes: wallet.publicKey.value.toString() } },
                            { dataSize: 200 }
                        ]
                    }
                )
                stakeAccounts.value = stakeAccountInfo
            } catch (error) {
                console.error('[MevRewardsClaimer] Error getting stake accounts:', error)
            } finally {
                loadingAccounts.value = false
            }
        }

        const getValidators = async () => {
            try {
                const response = await axios.get('/validators/ajax', {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' }
                })
                validatorData.value = response.data
            } catch (error) {
                console.error('[MevRewardsClaimer] Error getting validator data:', error)
            }
        }

        const getEpochInfo = async () => {
            try {
                epochInfo.value = await connection.getEpochInfo()
                currentEpochInfo.value = epochInfo.value
                currentEpochStartSlot.value = epochInfo.value.absoluteSlot - epochInfo.value.slotIndex

                const samples = await connection.getRecentPerformanceSamples(60)
                const timePerSlotSamples = samples
                    .filter(sample => sample.numSlots !== 0)
                    .slice(0, 60)
                    .map(sample => sample.samplePeriodSecs / sample.numSlots)

                const avg = timePerSlotSamples.reduce((sum, cur) => sum + cur, 0) / timePerSlotSamples.length
                avgSlotTime.value = avg
                const slotsRemaining = epochInfo.value.slotsInEpoch - epochInfo.value.slotIndex
                const msRemaining = slotsRemaining * avg * 1000

                const hours = Math.floor(msRemaining / (1000 * 60 * 60))
                const minutes = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60))
                timeLeftInEpoch.value = `${hours}h ${minutes}m`
            } catch (error) {
                console.error('[MevRewardsClaimer] Error getting epoch info:', error)
            }
        }

        const getSolPrice = async () => {
            try {
                const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd')
                solPrice.value = response.data.solana.usd
            } catch (error) {
                console.error('[MevRewardsClaimer] Error fetching SOL price:', error)
            }
        }

        // Rent-exempt minimum for a stake account (200 bytes data + overhead)
        const STAKE_RENT_EXEMPT = 2282880 // ~0.00228 SOL

        const stakeAccountsWithMev = computed(() => {
            return stakeAccounts.value.map(account => {
                const activeStake = account.account.data.parsed.info.stake?.delegation?.stake || 0
                const totalBalance = account.account.lamports
                // Subtract rent-exempt minimum — it's not withdrawable while account is active
                const mevBalance = Math.max(0, totalBalance - activeStake - STAKE_RENT_EXEMPT) / 1e9
                const validator = validatorData.value[account.account.data.parsed.info.stake?.delegation?.voter] || {}

                return {
                    ...account,
                    validator,
                    mevBalance,
                    pendingMev: 0,
                    progress: 0
                }
            })
        })

        const totalClaimableMev = computed(() => {
            return stakeAccountsWithMev.value.reduce((total, account) => total + account.mevBalance, 0)
        })

        const subscribeToSlotUpdates = () => {
            if (unmounted) return

            // Close existing connection before creating a new one
            if (wsConnection && wsConnection.readyState !== WebSocket.CLOSED) {
                wsConnection.close()
            }

            const ws = new WebSocket(wsUrl)

            ws.onopen = () => {
                ws.send(JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'slotSubscribe' }))
            }

            ws.onmessage = (event) => {
                const message = JSON.parse(event.data)
                if (message.method === 'slotNotification') {
                    currentSlot.value = message.params.result.slot
                    if (currentEpochInfo.value && currentSlot.value > (currentEpochInfo.value.absoluteSlot + currentEpochInfo.value.slotsInEpoch)) {
                        Promise.all([getEpochInfo(), updateLeaderSchedule()])
                    }
                }
            }

            ws.onerror = () => {}

            ws.onclose = () => {
                if (!unmounted) setTimeout(subscribeToSlotUpdates, 3000)
            }

            wsConnection = ws
            return ws
        }

        const updateLeaderSchedule = async () => {
            try {
                currentSchedule.value = await connection.getLeaderSchedule()
            } catch (error) {
                console.error('[MevRewardsClaimer] Error getting leader schedule:', error)
            }
        }

        const updateLeaderProgress = () => {
            try {
                if (!currentSchedule.value || !currentSlot.value) return
                const idAccounts = []
                stakeAccountsWithMev.value.forEach(account => {
                    const validator = account.validator
                    idAccounts.push(validator.account)
                    let validatorSchedule = currentSchedule.value[validator.account]
                    if (!validator || !validatorSchedule) return

                    const totalSlots = validatorSchedule.length
                    const passedSlots = validatorSchedule
                        .filter(index => (index + currentEpochStartSlot.value) <= currentSlot.value).length
                    account.progress = (passedSlots / totalSlots) * 100
                })

                const mevSlots = []
                Object.keys(idAccounts).forEach((id) => {
                    let vSlots = currentSchedule.value[idAccounts[id]] || null
                    if (!vSlots) return
                    vSlots.forEach((idx) => {
                        let slot = idx + currentEpochStartSlot.value
                        if (!mevSlots.includes(slot)) mevSlots.push(slot)
                    })
                })
                totalMevSlots.value = mevSlots.length
                nextTipSlot.value = mevSlots.filter(slot => slot > currentSlot.value)[0] || Infinity
            } catch (error) {
                console.error('[MevRewardsClaimer] Error updating leader progress:', error)
            }
        }

        const updatePendingMev = async () => {
            try {
                const firstSlot = epochInfo.value.absoluteSlot - epochInfo.value.slotIndex
                const validators = stakeAccountsWithMev.value
                    .map(a => {
                        const voteAccount = a.account.data.parsed.info.stake?.delegation?.voter
                        return validatorData.value[voteAccount]?.account
                    })
                    .filter(Boolean)
                let vals = collect(validators).unique()

                if (vals.length === 0) return

                const response = await axios.get('/blocks/query', {
                    params: {
                        "filter[slots]": firstSlot + "," + epochInfo.value.absoluteSlot,
                        "filter[leader.leader]": vals.join(','),
                        per_page: 10000
                    }
                })
                const blockData = response.data.data || []
                blocks.value = blockData

                const validatorList = stakeAccountsWithMev.value
                    .map(a => validatorData.value[a.account.data.parsed.info.stake?.delegation?.voter])
                    .filter(Boolean)

                let totalScheduledBlocks = 0
                let completedBlockCount = 0
                validatorList.forEach(validator => {
                    if (!currentSchedule.value || !validator.vote_account) return
                    const scheduledBlocks = currentSchedule.value[validator.vote_account] || []
                    totalScheduledBlocks += scheduledBlocks.length
                    completedBlockCount += scheduledBlocks.filter(slot => slot <= currentSlot.value).length
                })
                completedBlocks.value = completedBlockCount
                totalBlocks.value = totalScheduledBlocks
                blockProgress.value = totalScheduledBlocks > 0
                    ? Math.round((completedBlockCount / totalScheduledBlocks) * 100)
                    : 0

                const validatorTips = collect(blockData).groupBy('leader.leader').map(group => {
                    const validator = Object.values(validatorData.value).find(v => v.account === group.first().leader.leader)
                    const jitoCommission = validator?.jito_commission || 0
                    const commissionMultiplier = 1 - (jitoCommission / 10000)
                    return group.sum(slot => slot.fees?.jito_total || 0) * commissionMultiplier
                }).all()

                const validatorStake = vals.map((account) => {
                    let val = Object.values(validatorData.value).find(v => v.account === account)
                    return { vote_account: val.vote_account, stake: val.active_stake || 0 }
                }).pluck('stake', 'vote_account')

                stakeAccountsWithMev.value.forEach(account => {
                    const voteAccount = account.account.data.parsed.info.stake?.delegation?.voter
                    if (!voteAccount) return
                    const validator = validatorData.value[voteAccount] || null
                    if (!validator) return
                    const tips = validatorTips[validator.account] || 0
                    const totalStake = validatorStake.items[voteAccount] || 0
                    const userStakeShare = account.account.data.parsed.info.stake?.delegation?.stake / totalStake
                    account.pendingMev = tips * userStakeShare
                })

                pendingMev.value = stakeAccountsWithMev.value.reduce((sum, acc) => sum + acc.pendingMev, 0) / 1e9
            } catch (error) {
                console.error('[MevRewardsClaimer] Error updating pending MEV:', error)
            }
        }

        const updateNextTipCountdown = () => {
            if (nextTipSlot.value === Infinity || !avgSlotTime.value) {
                nextTipCountdown.value = 'No scheduled blocks'
                return
            }
            const slotsUntilNext = nextTipSlot.value - currentSlot.value
            if (slotsUntilNext <= 0) {
                nextTipCountdown.value = 'Any moment...'
                return
            }
            const secondsUntilNext = slotsUntilNext * avgSlotTime.value
            const minutes = Math.floor(secondsUntilNext / 60)
            const seconds = Math.floor(secondsUntilNext % 60)
            nextTipCountdown.value = `${minutes}m ${seconds}s`
        }

        const claimMevRewards = async () => {
            if (!wallet.connected.value) return

            claiming.value = true
            claimError.value = null
            claimSuccess.value = null
            try {
                const claimableAccounts = stakeAccountsWithMev.value.filter(account => account.mevBalance > 0)
                if (claimableAccounts.length === 0) return

                const instructions = claimableAccounts
                    .filter(account => Math.floor(account.mevBalance * 1e9) > 0)
                    .map(account => {
                        return StakeProgram.withdraw({
                            stakePubkey: account.pubkey,
                            authorizedPubkey: wallet.publicKey.value,
                            toPubkey: wallet.publicKey.value,
                            lamports: Math.floor(account.mevBalance * 1e9)
                        })
                    })

                if (instructions.length === 0) {
                    claimError.value = 'No claimable MEV rewards found'
                    return
                }

                const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash()
                const transaction = new Transaction({
                    feePayer: wallet.publicKey.value,
                    recentBlockhash: blockhash,
                    lastValidBlockHeight
                }).add(...instructions)

                const signed = await wallet.signTransaction(transaction)
                const txid = await connection.sendRawTransaction(signed.serialize())
                await connection.confirmTransaction(txid)

                const totalClaimed = claimableAccounts.reduce((sum, a) => sum + a.mevBalance, 0)
                claimSuccess.value = {
                    amount: totalClaimed.toFixed(4),
                    explorerUrl: `https://explorer.solana.com/tx/${txid}`
                }

                // Refresh data
                getStakeAccounts()
            } catch (error) {
                claimError.value = error.message || 'Failed to claim MEV rewards'
                console.error('[MevRewardsClaimer] Error claiming MEV rewards:', error)
            } finally {
                claiming.value = false
            }
        }

        const calculateUserShare = (block) => {
            const validator = Object.values(validatorData.value).find(v => v.account === block.leader?.leader)
            if (!validator) return 0

            const jitoCommission = validator.jito_commission || 0
            const commissionMultiplier = 1 - (jitoCommission / 10000)
            const tips = (block.fees?.jito_total || 0) / 1e9 * commissionMultiplier

            const account = stakeAccountsWithMev.value.find(a =>
                validatorData.value[a.account.data.parsed.info.stake?.delegation?.voter]?.account === block.leader?.leader
            )
            if (!account) return 0

            const totalStake = validator.active_stake || 1
            const userStakeShare = account.account.data.parsed.info.stake?.delegation?.stake / totalStake
            return tips * userStakeShare
        }

        const initializeData = async () => {
            await Promise.all([
                getStakeAccounts(),
                getValidators(),
                getEpochInfo(),
                updateLeaderSchedule(),
                getSolPrice()
            ])
            updatePendingMev()
        }

        onMounted(async () => {
            if (wallet.connected.value) {
                await initializeData()
            } else {
                loadingAccounts.value = false
            }

            intervals.push(setInterval(updatePendingMev, 60000))
            intervals.push(setInterval(updateLeaderProgress, 5000))
            intervals.push(setInterval(updateNextTipCountdown, 1000))

            subscribeToSlotUpdates()
        })

        onUnmounted(() => {
            unmounted = true
            intervals.forEach(id => clearInterval(id))
            intervals = []
            if (wsConnection && wsConnection.readyState !== WebSocket.CLOSED) {
                wsConnection.close()
            }
        })

        watch(
            () => wallet.connected.value,
            async (connected) => {
                if (connected) {
                    await initializeData()
                } else {
                    stakeAccounts.value = []
                }
            }
        )

        return {
            walletConnected: computed(() => wallet.connected.value),
            loadingAccounts,
            solPrice,
            claiming,
            claimError,
            claimSuccess,
            stakeAccountsWithMev,
            totalClaimableMev,
            pendingMev,
            nextTipCountdown,
            timeLeftInEpoch,
            claimMevRewards,
            blocks,
            recentBlocks,
            calculateUserShare,
            totalMevSlots,
            blockProgressPct,
            blockProgress,
            validatorData,
            shortenAddress,
        }
    }
}
</script>
